import { useRef, useState, useEffect } from "react";
import { Form, Modal } from "react-bootstrap";
import { FocusScope } from "@react-aria/focus";
import useOutsideClicked from "./useOutsideClicked";
import useMobileScreen from "./useMobileScreen";

// items : array of { value, label } objects
// title : label for checkbox / placeholder
//
// maxSelection === 1  → single-select: onChange(item) fires with a single { value, label } object
// maxSelection === null → unlimited multi-select
// maxSelection > 1   → limited multi-select
//
// onChange — optional callback invoked whenever the selection changes:
//   • single-select (maxSelection === 1): called with a single { value, label } object
//   • multi-select  (maxSelection !== 1): called with an array of { value, label } objects
//   Omit it to use the component as uncontrolled (selection is managed internally).
//
// value — optional controlled selection state:
//   • single-select: a single { value, label } object (or undefined/null for nothing selected)
//   • multi-select : an array of { value, label } objects
//   When provided, the component is "controlled" — the parent owns the selection and must
//   update it via onChange. When omitted, the component manages selection internally (uncontrolled).

/**
 * A searchable dropdown that supports single-select and multi-select modes.
 * Can be used as controlled (pass `value`) or uncontrolled (omit `value`).
 */
function DropdownCheckboxGeneric({ items, onChange, title, maxSelection = null, value }) {
    const wrapperRef = useRef(null);
    const dropdownButtonRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [userInput, setUserInput] = useState("");
    const [filteredItems, setFilteredItems] = useState([]);
    const [internalSelected, setInternalSelected] = useState([]);
    const [buttonLabel, setButtonLabel] = useState(title);
    const isMobile = useMobileScreen();

    const isSingleSelect = maxSelection === 1;
    const isControlled = value !== undefined;
    const selected = isControlled ? (value ?? []) : internalSelected;

    const closeDropdown = () => setIsOpen(false);
    const toggleDropdown = () => setIsOpen(v => !v);

    useOutsideClicked(wrapperRef, dropdownButtonRef, closeDropdown);

    useEffect(() => {
        if (userInput.trim()) {
            setFilteredItems(items.filter(item =>
                item.label.toLowerCase().includes(userInput.trim().toLowerCase())
            ));
        } else {
            setFilteredItems(items);
        }
    }, [userInput, items]);

    useEffect(() => {
        setButtonLabel(
            selected.length === 0
                ? title
                : isSingleSelect
                    ? items.find(i => i.value === selected[0]?.value)?.label ?? title
                    : `${selected.length} selected`
        );
    }, [selected]);

    const updateSelection = (next) => {
        if (!isControlled) setInternalSelected(next);
        onChange?.(next);
    };

    const isChecked = (item) => {
        if (isSingleSelect) {
            const sel = isControlled ? value : internalSelected[0];
            return sel?.value === item.value;
        }
        return selected.some(s => s.value === item.value);
    };

    const isDisabled = (item) =>
        !isSingleSelect &&
        maxSelection != null &&
        !isChecked(item) &&
        selected.length >= maxSelection;

    const handleSelect = (event, item) => {
        if (isSingleSelect) {
            if (!isControlled) setInternalSelected([item]);
            onChange?.(item);
            closeDropdown();
            return;
        }
        if (!item) {
            updateSelection(event.target.checked ? filteredItems : []);
        } else {
            if (event.target.checked) {
                updateSelection([...selected, item]);
            } else {
                updateSelection(selected.filter(s => s.value !== item.value));
            }
        }
    };

    const handleKeyPress = (e) => { 
        if (e.keyCode === 13) {
            toggleDropdown()
        }
        if (e.keyCode === 27) {
            closeDropdown()
        }
    };

    const isAllSelected = filteredItems.length > 0 && filteredItems.every(isChecked);

    const dropdownContent = (
        <>
            <div className="dropdown-checkbox">
                <i>
                    <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M13 13L19 19M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z" stroke="#333333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </i>
                <input type="text" onChange={e => setUserInput(e.target.value)} />
            </div>
            <Form>
                {!isSingleSelect && (
                    <div className="checkbox-item">
                        <input type="checkbox" id="generic-select-all" checked={isAllSelected} onChange={e => handleSelect(e, null)} />
                        <label htmlFor="generic-select-all">Select All</label>
                    </div>
                )}
                {filteredItems.map(item => (
                    isSingleSelect ? (
                        <div
                            className="checkbox-item"
                            key={item.value}
                            onClick={() => handleSelect(null, item)}
                            style={{ cursor: "pointer", fontWeight: isChecked(item) ? "bold" : undefined }}
                        >
                            {item.label}
                        </div>
                    ) : (
                        <div className="checkbox-item" key={item.value}>
                            <input
                                type="checkbox"
                                id={`generic-item-${item.value}`}
                                checked={isChecked(item)}
                                disabled={isDisabled(item)}
                                onChange={e => handleSelect(e, item)}
                            />
                            <label htmlFor={`generic-item-${item.value}`}>{item.label}</label>
                        </div>
                    )
                ))}
            </Form>
        </>
    );

    return (
        <div className="dropdown-checkbox-wrapper" ref={wrapperRef}>
            <div ref={dropdownButtonRef} onKeyDown={e => handleKeyPress(e)} className="dropdown-checkbox" onClick={toggleDropdown}>
                <i>
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M0.292893 0.292893C0.683417 -0.0976311 1.31658 -0.0976311 1.70711 0.292893L5 3.58579L8.29289 0.292893C8.68342 -0.0976311 9.31658 -0.0976311 9.70711 0.292893C10.0976 0.683417 10.0976 1.31658 9.70711 1.70711L5.70711 5.70711C5.31658 6.09763 4.68342 6.09763 4.29289 5.70711L0.292893 1.70711C-0.0976311 1.31658 -0.0976311 0.683417 0.292893 0.292893Z" fill="#333333" />
                    </svg>
                </i>
                <input type="button" className="text-end" value={buttonLabel} />
            </div>

            {isOpen && (
                isMobile ? (
                    <Modal show={true} onHide={closeDropdown} className="modal-dropdown-checkbox-select">
                        <Modal.Header closeButton>
                            {/* <Modal.Title role="heading">{title}</Modal.Title> */}
                        </Modal.Header>
                        <Modal.Body className="dropdown-checkbox-list">
                            {!isSingleSelect && <h6 className="mb-3">Selected: {selected.length}</h6>}
                            {dropdownContent}
                        </Modal.Body>
                    </Modal>
                ) : (
                    <FocusScope contain restoreFocus autoFocus>
                        <div role="dialog" ref={wrapperRef} className="dropdown-checkbox-list" onKeyDown={handleKeyPress}>
                            <div className="d-flex justify-content-between dropdown-title">
                                {/* <span role="heading">{title}</span> */}
                            </div>
                            {!isSingleSelect && <h6 className="mb-3">Selected: {selected.length}</h6>}
                            {dropdownContent}
                        </div>
                    </FocusScope>
                )
            )}
        </div>
    );
}

export default DropdownCheckboxGeneric;
